import React, { useState, useEffect, useMemo } from 'react';
import { Col, Row, Text, Button, CMSLayout, useUIState } from 'components';
import { IScreen } from 'type';
import { useNavFunc } from 'navigation';
import { SCREEN } from 'const';
import { ChatMessagesContent } from './ChatMessagesContent';
import { FontAwesome5 } from '@expo/vector-icons';
import Store from 'store';
import { ActivityIndicator } from 'react-native';

interface OrderIssue {
  id: string;
  orderId: string;
  userId: string;
  type: 'missing' | 'wrong-item' | 'damaged' | 'other';
  description: string;
  status: 'pending' | 'in-progress' | 'resolved' | 'rejected';
  attachFiles?: string[];
  createdAt?: string;
  updatedAt?: string;
  order?: { sorId?: string };
}

const OrderIssueDetail: IScreen = () => {
  const { navigation, route } = useNavFunc();
  // @ts-ignore
  const { issueId, noSidebar: noSidebarParam, noHeader: noHeaderParam } = route.params || {};
  const noSidebarFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    const v = urlParams.get('noSidebar');
    return v === '1' || v === 'true' || v === 'yes';
  }, []);
  const noHeaderFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    const v = urlParams.get('noHeader');
    return v === '1' || v === 'true' || v === 'yes';
  }, []);
  const noSidebar = noSidebarParam ?? noSidebarFromUrl;
  const noHeader = noHeaderParam ?? noHeaderFromUrl;
  const [issue, setIssue] = useState<OrderIssue | null>(null);
  const [{ fetching, errorMes }, setUI] = useUIState({
    fetching: true,
    errorMes: '',
  });

  const goBack = () => {
    navigation.canGoBack()
      ? navigation.goBack()
      : navigation.navigate(SCREEN.ListOrderIssues);
  };

  const getIssue = async () => {
    if (!issueId) {
      setUI({ fetching: false, errorMes: 'Issue ID is required' });
      return;
    }

    try {
      setUI({ fetching: true, errorMes: '' });

      const res = await Store.Api.Order.getOrderIssue({
        id: issueId,
      });

      if (res.data.success) {
        setIssue(res.data.data);
        setUI({ fetching: false, errorMes: '' });
      } else {
        setUI({ fetching: false, errorMes: res.data.error || 'Failed to load order issue' });
      }
    } catch (err) {
      setUI({ fetching: false, errorMes: String(err) });
    }
  };

  useEffect(() => {
    getIssue();
  }, [issueId]);

  if (!issueId) {
    return (
      <CMSLayout noSidebar={noSidebar} noHeader={noHeader}>
        <Col flex1 middle>
          <Text>Issue ID is required</Text>
          <Button text="Go Back" onPress={goBack} mt2 />
        </Col>
      </CMSLayout>
    );
  }

  return (
    <CMSLayout noSidebar={noSidebar} noHeader={noHeader}>
      <Col flex1>
        <Row m2 marginBottom={0} alignItems="center">
          <Button
            solid
            height={30}
            paddingHorizontal={8}
            iconLeft={<FontAwesome5 name="chevron-left" size={12} color="white" />}
            text="Back"
            fitContent
            mr1
            onPress={goBack}
            borderRadius={15}
          />
          <Text h3>Order Issue Detail</Text>
        </Row>
        {errorMes ? (
          <Col flex1 m2 mv1 p2 round1 bgWhite middle>
            <Text color="#ff4d4f">{errorMes}</Text>
            <Button text="Go Back" onPress={goBack} mt2 />
          </Col>
        ) : fetching ? (
          <Col flex1 m2 mv1 p2 round1 bgWhite middle>
            <ActivityIndicator size="large" />
            <Text mt2>Loading order issue...</Text>
          </Col>
        ) : issue ? (
          <Col flex1 m2 mv1 p2 round1 bgWhite style={{ overflow: 'hidden' }}>
            <ChatMessagesContent
              issueId={issueId}
              issue={issue}
              onStatusUpdate={() => {
                // Refresh the issue data after status update
                getIssue();
              }}
            />
          </Col>
        ) : null}
      </Col>
    </CMSLayout>
  );
};

OrderIssueDetail.routeInfo = {
  title: 'Order Issue Detail',
  path: '/order-issues/:issueId',
};

export default OrderIssueDetail;

